import { useReducer, useEffect, useCallback } from 'react';

import { useLocation, useParams } from 'react-router';

import CustomCampaign from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { setDraftState } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import {
    customCurationReducer,
    directMailInitialState,
    emailInitialState,
    smsInitialState,
    voiceCallInitialState,
    CampaignStateTypes,
} from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels } from 'domains/campaigns/types';
import { transformDraftSendFromApi } from 'domains/campaigns/utils';
import useDraftCampaign from 'hooks/queries/useDraftCampaign';

const CustomPage = () => {
    const location: { state?: { channel?: CampaignChannels } } = useLocation();
    const { campaignId } = useParams<{ campaignId?: string }>();

    const channel = location?.state?.channel ?? CampaignChannels.EMAIL;

    const { data: campaignData, isSuccess } = useDraftCampaign(campaignId || '', {
        enabled: !!campaignId,
    });

    const getInitialState = useCallback((): CampaignStateTypes => {
        if (channel === CampaignChannels.DIRECT_MAIL) return directMailInitialState;
        if (channel === CampaignChannels.EMAIL) return emailInitialState;
        if (channel === CampaignChannels.SMS) return smsInitialState;
        if (channel === CampaignChannels.VOICE_CALL) return voiceCallInitialState;
    }, [channel]);

    const initialState = getInitialState();
    const [state, dispatch] = useReducer(customCurationReducer, initialState);

    useEffect(() => {
        if (isSuccess && campaignData && campaignId) {
            const transformedSends = (campaignData.sends || []).map((apiSend: any, _index: number, allSends: any[]) =>
                transformDraftSendFromApi(apiSend, allSends)
            );

            const hasSends = transformedSends.length > 0;
            const initialSends = getInitialState().sends;

            const campaignState: CampaignStateTypes = {
                name: campaignData.name || '',
                type: campaignData.type,
                sends: hasSends ? transformedSends : initialSends,
                ...(campaignData?.reentryDetails && { reentryDetails: campaignData?.reentryDetails }),
                ...(campaignData?.abTestConfig && { abTestConfig: campaignData?.abTestConfig }),
            };

            dispatch(setDraftState(campaignState));
        }
    }, [isSuccess, campaignData, campaignId, getInitialState]);

    return <CustomCampaign campaignState={state} dispatchCampaignAction={dispatch} campaignId={campaignId} />;
};

export default CustomPage;
